// ============================================================
// Dashboard.js — Refactored
// ✅ Bug fix: Swap visual notifications restored
// ✅ Guild config: dynamic & scalable (no more hardcoded names)
// ✅ Code: organized into constants, hooks, helpers, render
// ✅ UI: improved toolbar, layout, responsive
// ============================================================

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Table, Button, Badge, Collapse, Space, Switch, ConfigProvider, theme,
  Segmented, Tabs, Dropdown, message, notification, Tooltip,
} from 'antd';
import {
  BellOutlined, CopyOutlined, UserOutlined, HeartOutlined, SettingOutlined,
  BulbOutlined, BulbFilled, ClearOutlined, RadarChartOutlined, MailOutlined,
  MinusOutlined, FireOutlined, AimOutlined, SmileOutlined, TeamOutlined,
  UnorderedListOutlined, AlertOutlined, SoundOutlined, ArrowUpOutlined,
  ArrowDownOutlined, SyncOutlined, UserSwitchOutlined, GlobalOutlined,
  InfoCircleOutlined, DashboardOutlined, SwapOutlined,
} from '@ant-design/icons';
import { SpeedInsights } from '@vercel/speed-insights/react';

import ClassificacaoModal from './ClassificacaoModal';
import AlertasModal from './AlertasModal';
import AlertConfig from './AlertConfig';
import Avisos from './Avisos';
import AdminPanelModal from './AdminPanelModal';
import CharacterScanModal from './CharacterScanModal';
import ContactForm from './ContactForm';
import StatusModal from './StatusModal';
import { endpoints } from './caminhoApi';
import './App.css';
import './Dashboard.css'; // NEW: separate dashboard styles

// ============================================================
// CONSTANTS — edit here to add/remove guilds or vocations
// ============================================================

const CURRENT_VERSION = '1.0.20';

/**
 * GUILD_CONFIG drives all guild-specific behavior.
 * To add a new guild: add an entry here AND to GUILDS below.
 *
 * alarmKey: must match the key used in AlertConfig's localStorage object
 *   (kept for backward compatibility — rename both here and in AlertConfig.js
 *    when you're ready to break the old localStorage schema)
 */
const GUILD_CONFIG = {
  'Eternity': {
    className: 'guild-one',
    notifColor: '#c2410c',
    alarmKey: 'eternityAlarm',  // localStorage key — see AlertConfig.js
    swapRef: 'Guild1',           // internal ref key for swap tracking
  },
  'Namelesss': {
    className: 'guild-two',
    notifColor: '#7e22ce',
    alarmKey: 'namelesssAlarm', // localStorage key — see AlertConfig.js
    swapRef: 'Guild2',
  },
};

/** Ordered list of guilds to render. Must match GUILD_CONFIG keys. */
const GUILDS = Object.keys(GUILD_CONFIG);

/** Player classifications shown in the collapse panels (order matters). */
const CLASSIFICATIONS = ['Prioridade', 'Bomba', 'Hunted', 'Main', 'Maker'];

/** Vocation abbreviations and icons. */
const VOCATION_MAP = {
  'Elite Knight':    { short: 'EK', icon: <UserOutlined /> },
  'Elder Druid':     { short: 'ED', icon: <HeartOutlined /> },
  'Master Sorcerer': { short: 'MS', icon: <FireOutlined /> },
  'Royal Paladin':   { short: 'RP', icon: <AimOutlined /> },
  'Exalted Monk':    { short: 'EM', icon: <SmileOutlined /> },
};

// ============================================================
// PURE HELPER FUNCTIONS
// ============================================================

const formatTime = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h > 0 ? `${h}:` : ''}${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const normalize = (text) =>
  text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();

const getVocationCounts = (players) =>
  players.reduce((acc, p) => {
    acc[p.vocation] = (acc[p.vocation] || 0) + 1;
    return acc;
  }, {});

/** Read alertConfig from localStorage (set by AlertConfig component). */
const readLocalAlertConfig = () => {
  try {
    const raw = localStorage.getItem('alertConfig');
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

/** Check if alerts are enabled for a specific guild. */
const isGuildAlarmEnabled = (guildName) => {
  const cfg = readLocalAlertConfig();
  const key = GUILD_CONFIG[guildName]?.alarmKey;
  return key ? (cfg[key] ?? true) : true;
};

/** Row background color based on login time. */
const getLoginTimeStyle = (loginTime, darkMode) => {
  if (loginTime <= 120)  return { backgroundColor: darkMode ? '#662222' : '#ffcccc' };
  if (loginTime <= 300)  return { backgroundColor: darkMode ? '#665214' : '#fff0b3' };
  if (loginTime > 36000) return { backgroundColor: darkMode ? '#443266' : '#e0ccff' };
  return {};
};

// ============================================================
// CUSTOM HOOKS
// ============================================================

/** Persists dark mode to localStorage and syncs body background. */
function useTheme() {
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });

  useEffect(() => {
    document.body.style.backgroundColor = darkMode ? '#141414' : '#f0f2f5';
  }, [darkMode]);

  const toggleTheme = useCallback((checked) => {
    setDarkMode(checked);
    localStorage.setItem('darkMode', checked ? 'true' : 'false');
  }, []);

  return { darkMode, toggleTheme };
}

/** Auto-reloads the page when a new version is deployed. */
function useVersionCheck() {
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        const data = await res.json();
        if (data.version !== CURRENT_VERSION) {
          console.log(`New version detected (${data.version}) — reloading`);
          window.location.reload(true);
        }
      } catch (err) {
        console.error('Version check failed:', err);
      }
    }, 1_800_000);
    return () => clearInterval(interval);
  }, []);
}

/** Polls the admin request count every 10 minutes. */
function useRequestCount() {
  const [requestCount, setRequestCount] = useState(0);

  const load = useCallback(() => {
    axios.get(endpoints.requestCount)
      .then(res => setRequestCount(res.data.count || 0))
      .catch(() => setRequestCount(0));
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 10 * 60_000);
    return () => clearInterval(interval);
  }, [load]);

  return { requestCount, reloadRequestCount: load };
}

/** Fetches and periodically refreshes extra player info (exp data). */
function useExtraInfo() {
  const [extraInfoByName, setExtraInfoByName] = useState({});

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(endpoints.extraInfoEndpoint);
        setExtraInfoByName(res.data);
      } catch (err) {
        console.error('Extra info fetch failed:', err);
      }
    };
    fetch();
    const interval = setInterval(fetch, 20 * 60_000);
    return () => clearInterval(interval);
  }, []);

  return extraInfoByName;
}

/** Fetches the list of players that are outside any guild. */
function useSemGuildList() {
  const [semGuildList, setSemGuildList] = useState([]);

  useEffect(() => {
    axios.get(endpoints.semGuildEndpoint)
      .then(res => setSemGuildList(res.data))
      .catch(err => console.error('semGuild fetch failed:', err));
  }, []);

  return { semGuildList, setSemGuildList };
}

/** Initializes the Web Worker that polls online players. */
function useOnlinePlayers() {
  const initialState = useMemo(
    () => GUILDS.reduce((acc, g) => ({ ...acc, [g]: [] }), {}),
    []
  );
  const [onlinePlayers, setOnlinePlayers] = useState(initialState);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const worker = new Worker(`${process.env.PUBLIC_URL}/playerWorker.js`);
    worker.postMessage({ type: 'init', guilds: GUILDS, apiEndpoint: endpoints.getOnlinePlayers, token });

    worker.onmessage = (e) => {
      if (e.data.type === 'players') {
        const grouped = GUILDS.reduce((acc, guild) => {
          acc[guild] = e.data.data.filter(p => p.guild === guild);
          return acc;
        }, {});
        setOnlinePlayers(grouped);
      } else if (e.data.type === 'error') {
        console.error('Worker error:', e.data.error);
      }
    };

    return () => worker.terminate();
  }, []);

  return onlinePlayers;
}

// ============================================================
// MAIN COMPONENT
// ============================================================

function Dashboard() {
  // --- Theme ---
  const { darkMode, toggleTheme } = useTheme();
  useVersionCheck();

  // --- Data ---
  const onlinePlayers   = useOnlinePlayers();
  const extraInfoByName = useExtraInfo();
  const { semGuildList, setSemGuildList } = useSemGuildList();
  const { requestCount, reloadRequestCount } = useRequestCount();

  // --- Player data ---
  const [playerClassification, setPlayerClassification] = useState({});
  const [alertConfig, setAlertConfig]     = useState({});
  const [selectedVocation, setSelectedVocation] = useState(
    () => GUILDS.reduce((acc, g) => ({ ...acc, [g]: 'All' }), {})
  );

  // --- Modal visibility ---
  const [modals, setModals] = useState({
    classification: false,
    alertConfig: false,
    alertSound: false,
    status: false,
    admin: false,
    character: false,
    avisos: true,
  });

  const [scanCharacterName, setScanCharacterName] = useState('');
  const contactFormRef = useRef();
  const [api, contextHolder] = notification.useNotification();

  // Alert engine refs
  const playerCountRef = useRef({});
  const lastAlertsRef  = useRef({});

  // --- Modal helpers ---
  const openModal  = useCallback((key) => setModals(m => ({ ...m, [key]: true })), []);
  const closeModal = useCallback((key) => setModals(m => ({ ...m, [key]: false })), []);

  const openCharacterScan = useCallback((name) => {
    setScanCharacterName(name);
    openModal('character');
  }, [openModal]);

  // --- Data fetchers ---
  const fetchClassificacoes = useCallback(async () => {
    try {
      const { data } = await axios.get(endpoints.classEndpoint);
      const converted = Object.fromEntries(
        Object.entries(data).map(([name, info]) => [
          name,
          typeof info === 'string' ? info : info.tipo,
        ])
      );
      setPlayerClassification(converted);
    } catch (err) {
      console.error('Classification fetch failed:', err.message);
    }
  }, []);

  const fetchAlertConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(endpoints.alertasEndpoint);
      setAlertConfig(data);
    } catch (err) {
      console.error('AlertConfig fetch failed:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchClassificacoes();
    fetchAlertConfig();
    notification.config({ placement: 'bottomRight' });
  }, [fetchClassificacoes, fetchAlertConfig]);

  // --- Player filter ---
  const getPlayersByClassification = useCallback((players, classification, selectedVoc) => {
    const normalizedMap = Object.fromEntries(
      Object.entries(playerClassification).map(([k, v]) => [normalize(k), v])
    );
    return players
      .filter(p => {
        const vocMatch = selectedVoc === 'All' || p.vocation === selectedVoc;
        const cls = normalizedMap[normalize(p.name)] || 'Hunted';
        return cls === classification && vocMatch;
      })
      .sort((a, b) => (a.loginTime || 0) - (b.loginTime || 0));
  }, [playerClassification]);

  // --- Audio ---
  const playSound = useCallback((src, repeat) => {
    const { volume = 80 } = readLocalAlertConfig();
    for (let i = 0; i < repeat; i++) {
      setTimeout(() => {
        const audio = new Audio(src);
        audio.volume = volume / 100;
        audio.play().catch(err => console.error('Sound error:', err));
      }, i * 1_000);
    }
  }, []);

  // --- Notification helper ---
  const showNotification = useCallback(({ title, description, guildName, icon }) => {
    const cfg = GUILD_CONFIG[guildName] || {};
    api.open({
      message: <div style={{ color: '#000' }}>{title}</div>,
      description: (
        <div style={{ color: '#000' }}>
          {description}
          <div style={{ textAlign: 'right', marginTop: 6 }}>
            <Button type="primary" size="small" onClick={() => api.destroy()} icon={<ClearOutlined />}>
              Limpiar
            </Button>
          </div>
        </div>
      ),
      icon,
      duration: null,
      className: cfg.className || '',
    });
  }, [api]);

  // --- Alert engine ---
  const handleAlerts = useCallback(() => {
    const now = Math.floor(Date.now() / 1000);
    const localCfg = readLocalAlertConfig();
    const { volume = 80 } = localCfg;
    const swapKey = 'Swap';
    const swapConfig = alertConfig[swapKey];

    // ── Classification alerts ──────────────────────────────
    Object.keys(alertConfig).forEach(classification => {
      if (classification === swapKey) return;
      const { minPlayers, repeat, sound, maxMinutes } = alertConfig[classification] || {};

      GUILDS.forEach(guild => {
        if (!isGuildAlarmEnabled(guild)) return;

        const guildPlayers = onlinePlayers[guild] || [];
        const filtered     = getPlayersByClassification(guildPlayers, classification, 'All');
        const recentPlayers = filtered.filter(p => (p.loginTime || 0) <= maxMinutes * 60);
        const currentCount  = recentPlayers.length;

        const alertKey     = `${guild}-${classification}`;
        const prevAlert    = lastAlertsRef.current[alertKey] || { count: 0, timestamp: 0 };
        const timeSince    = now - prevAlert.timestamp;

        const shouldAlert =
          currentCount >= minPlayers &&
          (timeSince >= maxMinutes * 60 || currentCount >= prevAlert.count + minPlayers);

        if (!shouldAlert) return;

        lastAlertsRef.current[alertKey] = { count: currentCount, timestamp: now };
        if (volume > 0) playSound(sound, repeat);

        const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        showNotification({
          guildName: guild,
          title: <>Alerta: <strong>{classification}</strong></>,
          description: (
            <>
              ({time}) {guild} conectou{' '}
              <strong style={{ color: '#e67c73' }}>{currentCount}</strong>{' '}
              <strong>{classification}</strong>(s) nos últimos{' '}
              <strong>{maxMinutes}</strong> minutos.
            </>
          ),
          icon: (
            <BellOutlined style={{ color: GUILD_CONFIG[guild]?.notifColor || '#333' }} />
          ),
        });
      });
    });

    // ── Swap alert ─────────────────────────────────────────
    if (!swapConfig) return;
    const { minPlayers, repeat, sound, maxMinutes } = swapConfig;
    const SWAP_CHECK_INTERVAL = 5; // seconds between swap evaluations
    const lastSwapTime = lastAlertsRef.current.lastSwapTime || 0;

    if (now - lastSwapTime < SWAP_CHECK_INTERVAL) return;
    lastAlertsRef.current.lastSwapTime = now;

    // Build per-guild totals dynamically
    GUILDS.forEach(guild => {
      const ref      = GUILD_CONFIG[guild].swapRef;
      const nowTotal = (onlinePlayers[guild] || []).length;
      const prevKey  = `${ref}-PrevTotal`;
      const prevTotal = playerCountRef.current[prevKey] ?? nowTotal;
      playerCountRef.current[`${ref}-Change`] = nowTotal - prevTotal;
      playerCountRef.current[prevKey]          = nowTotal;
    });

    if (!playerCountRef.current['Swap-StartTime']) {
      playerCountRef.current['Swap-StartTime'] = now;
      GUILDS.forEach(g => {
        playerCountRef.current[`${GUILD_CONFIG[g].swapRef}-SwapAccum`] = 0;
      });
    }

    const elapsed = now - playerCountRef.current['Swap-StartTime'];

    GUILDS.forEach(guild => {
      const ref = GUILD_CONFIG[guild].swapRef;
      playerCountRef.current[`${ref}-SwapAccum`] += playerCountRef.current[`${ref}-Change`] || 0;
    });

    const resetSwap = () => {
      playerCountRef.current['Swap-StartTime'] = now;
      GUILDS.forEach(g => {
        playerCountRef.current[`${GUILD_CONFIG[g].swapRef}-SwapAccum`] = 0;
      });
    };

    if (elapsed <= maxMinutes * 60) {
      // Check every pair of guilds for opposing swap patterns
      for (let i = 0; i < GUILDS.length; i++) {
        for (let j = 0; j < GUILDS.length; j++) {
          if (i === j) continue;
          const guildA   = GUILDS[i];
          const guildB   = GUILDS[j];
          const refA     = GUILD_CONFIG[guildA].swapRef;
          const refB     = GUILD_CONFIG[guildB].swapRef;
          const accumA   = playerCountRef.current[`${refA}-SwapAccum`];
          const accumB   = playerCountRef.current[`${refB}-SwapAccum`];

          // guildA gained players while guildB lost → possible swap
          if (accumA >= minPlayers && accumB <= -minPlayers) {
            if (volume > 0) playSound(sound, repeat);

            // ✅ BUG FIX: visual notification was missing — now shown
            const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            showNotification({
              guildName: guildA,
              title: <>Swap Detectado</>,
              description: (
                <>
                  ({time}){' '}
                  <strong style={{ color: '#52c41a' }}>{guildA}</strong> ganhou{' '}
                  <strong style={{ color: '#52c41a' }}>+{accumA}</strong> e{' '}
                  <strong style={{ color: '#e67c73' }}>{guildB}</strong> perdeu{' '}
                  <strong style={{ color: '#e67c73' }}>{accumB}</strong>{' '}
                  players nos últimos <strong>{maxMinutes}</strong> minutos.
                </>
              ),
              icon: <SwapOutlined style={{ color: '#f59e0b' }} />,
            });
            resetSwap();
            return;
          }
        }
      }
    } else {
      resetSwap();
    }
  }, [alertConfig, onlinePlayers, getPlayersByClassification, playSound, showNotification]);

  useEffect(() => {
    handleAlerts();
  }, [handleAlerts]);

  // --- Menu actions for player rows ---
  const handleMenuClick = useCallback((key, player) => {
    const name = player.name.replace(/[\s\u00A0\u2000-\u200B]+/g, ' ').trim();
    if (key === 'copy') {
      navigator.clipboard.writeText(`Exiva "${name}"`);
      message.success(`Exiva de ${name} copiada!`);
    }
    if (key === 'tibia-site') {
      window.open(`https://www.tibia.com/community/?name=${encodeURIComponent(name)}`, '_blank');
    }
  }, []);

  // ============================================================
  // RENDER HELPERS
  // ============================================================

  const renderPlayerIcon = useCallback((player) => {
    const formattedName = player.name.replace(/\s+/g, '_');
    const extraInfo     = extraInfoByName[formattedName];
    const isEnemy       = semGuildList.some(p => p.name.toLowerCase() === player.name.toLowerCase());

    if (isEnemy) {
      return (
        <Tooltip title="Enemy fuera de la guild">
          <UserSwitchOutlined style={{ color: '#ffa500', marginRight: 8 }} />
        </Tooltip>
      );
    }

    if (extraInfo) {
      const expTooltip = (
        <div>
          {[
            { label: 'Exp Ayer', value: extraInfo.expYesterday },
            { label: 'Exp 7 Días', value: extraInfo.exp7Days },
            { label: 'Exp 30 Días', value: extraInfo.exp30Days },
          ].map(({ label, value }) => (
            <div key={label} style={{ color: 'white' }}>
              {label}:{' '}
              <span style={{ color: value >= 0 ? '#28a745' : '#dc3545', fontWeight: 'bold', fontSize: 16 }}>
                {value.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      );

      const arrowIcon =
        extraInfo.exp30Days > 0 ? <ArrowUpOutlined style={{ color: 'green' }} />
        : extraInfo.exp30Days < 0 ? <ArrowDownOutlined style={{ color: 'red' }} />
        : <MinusOutlined style={{ color: '#8c8c8c' }} />;

      return <Tooltip title={expTooltip}><span style={{ marginRight: 8 }}>{arrowIcon}</span></Tooltip>;
    }

    return (
      <Tooltip title="Miembro nuevo o renombrado">
        <SyncOutlined style={{ color: '#007bff', marginRight: 8 }} />
      </Tooltip>
    );
  }, [extraInfoByName, semGuildList]);

  const renderPlayerTable = useCallback((players) => {
    const playersWithKey = players.map(p => ({ ...p, key: p.name }));

    const columns = [
      {
        title: '#',
        key: 'index',
        width: '4%',
        render: (_, record, index) => {
          const style = getLoginTimeStyle(record.loginTime || 0, darkMode);
          return (
            <div style={{ ...style, textAlign: 'center', height: '100%' }}>
              {index + 1}
            </div>
          );
        },
      },
      {
        title: 'Nick',
        dataIndex: 'name',
        key: 'name',
        width: '42%',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (_, record) => {
          const style       = getLoginTimeStyle(record.loginTime || 0, darkMode);
          const formattedName = record.name.replace(/\s+/g, '_');
          const menu = {
            items: [
              { key: 'tibia-site', icon: <GlobalOutlined />, label: 'Site Tibia', className: 'custom-menu-item' },
              { key: 'scan',       icon: <RadarChartOutlined />, label: 'Escanear personajes', className: 'custom-menu-item' },
              { key: 'copy',       icon: <CopyOutlined />, label: 'Copiar Exiva', className: 'custom-menu-item' },
            ],
            onClick: ({ key }) => {
              if (key === 'scan') openCharacterScan(formattedName);
              else handleMenuClick(key, record);
            },
          };
          return (
            <Dropdown menu={menu} trigger={['click']}>
              <strong className="hover-change-color" style={style}>
                {renderPlayerIcon(record)}
                {record.name}
              </strong>
            </Dropdown>
          );
        },
      },
      {
        title: 'Voc',
        dataIndex: 'vocation',
        key: 'vocation',
        width: '12%',
        sorter: (a, b) => a.vocation.localeCompare(b.vocation),
        render: (voc, record) => (
          <div style={getLoginTimeStyle(record.loginTime || 0, darkMode)}>
            {VOCATION_MAP[voc]?.short ?? voc}
          </div>
        ),
      },
      {
        title: 'Level',
        dataIndex: 'level',
        key: 'level',
        width: '12%',
        sorter: (a, b) => a.level - b.level,
        render: (level, record) => (
          <div style={getLoginTimeStyle(record.loginTime || 0, darkMode)}>{level}</div>
        ),
      },
      {
        title: 'Login',
        key: 'login',
        width: '12%',
        sorter: (a, b) => (a.loginTime || 0) - (b.loginTime || 0),
        render: (_, record) => (
          <div style={{ ...getLoginTimeStyle(record.loginTime || 0, darkMode), fontFamily: 'monospace' }}>
            {formatTime(record.loginTime || 0)}
          </div>
        ),
      },
    ];

    return (
      <ConfigProvider theme={{ algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
        <Table
          dataSource={playersWithKey}
          columns={columns}
          pagination={false}
          size="small"
          bordered
        />
      </ConfigProvider>
    );
  }, [darkMode, openCharacterScan, handleMenuClick, renderPlayerIcon]);

  const renderGuildTab = useCallback((guildName) => {
    const players       = onlinePlayers[guildName] || [];
    const vocationCounts = getVocationCounts(players);
    const guildCfg      = GUILD_CONFIG[guildName] || {};

    return (
      <ConfigProvider theme={{ algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
        <div style={{ paddingBottom: 15 }}>
          <SpeedInsights />

          <h2
            className={guildCfg.className || ''}
            style={{ marginBottom: 10, paddingLeft: 10 }}
          >
            {guildName}
            <span style={{ marginLeft: 10 }}>
              <Badge
                count={players.length}
                overflowCount={9999}
                style={{ backgroundColor: '#52c41a', fontSize: 14 }}
              />
            </span>
          </h2>

          <Segmented
            block
            size="small"
            value={selectedVocation[guildName]}
            onChange={val => setSelectedVocation(prev => ({ ...prev, [guildName]: val }))}
            style={{ marginBottom: 16 }}
            options={['All', ...Object.keys(VOCATION_MAP)].map(voc => ({
              label: voc === 'All'
                ? <><TeamOutlined /> All ({players.length})</>
                : <>{VOCATION_MAP[voc].icon} {VOCATION_MAP[voc].short} ({vocationCounts[voc] || 0})</>,
              value: voc,
            }))}
          />

          <Collapse
            ghost
            defaultActiveKey={CLASSIFICATIONS}
            items={CLASSIFICATIONS
              .map(cls => {
                const classified = getPlayersByClassification(players, cls, selectedVocation[guildName]);
                if (!classified.length) return null;
                return {
                  key: cls,
                  label: <span style={{ fontWeight: 'bold' }}>{cls} ({classified.length})</span>,
                  children: renderPlayerTable(classified),
                };
              })
              .filter(Boolean)}
          />
        </div>
      </ConfigProvider>
    );
  }, [onlinePlayers, darkMode, selectedVocation, getPlayersByClassification, renderPlayerTable]);

  // ============================================================
  // RENDER
  // ============================================================

  const handleFecharAviso = () => {
    const audio = new Audio('/silence.mp3');
    audio.play().catch(err => console.error('Sound unlock error:', err));
    closeModal('avisos');
  };

  return (
    <ConfigProvider theme={{ algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
      <>
        <Avisos visible={modals.avisos} onClose={handleFecharAviso} />
        {contextHolder}

        <div className={`dashboard-root ${darkMode ? 'dark' : 'light'}`}>

          {/* ── Header ─────────────────────────────────────────── */}
          <header className="dashboard-header">
            <div className="dashboard-header__title">
              <span className="enemy-label">Enemy List</span>
              <span className="dashboard-header__subtitle">Solidera / Havera</span>
              <Switch
                size="small"
                checked={darkMode}
                onChange={toggleTheme}
                checkedChildren={<BulbFilled />}
                unCheckedChildren={<BulbOutlined />}
                title="Alternar tema"
                className="theme-switch"
                style={{ marginLeft: 10 }}
              />
            </div>

            {/* ── Toolbar ──────────────────────────────────────── */}
            <Space className="dashboard-toolbar" wrap>
              {/* Public tools */}
              <Tooltip title="Escanear personajes ocultos">
                <Button
                  type="primary" size="small"
                  icon={<RadarChartOutlined />}
                  onClick={() => { setScanCharacterName(''); openModal('character'); }}
                />
              </Tooltip>

              <Tooltip title="Nuevos ingresos en guilds">
                <Button
                  type="primary" size="small"
                  icon={<InfoCircleOutlined />}
                  onClick={() => openModal('avisos')}
                />
              </Tooltip>

              <AlertConfig visible={modals.alertSound} onClose={() => closeModal('alertSound')}>
                <Tooltip title="Activar/Desactivar alertas y volumen">
                  <Button
                    type="primary" size="small"
                    icon={<SoundOutlined />}
                    onClick={() => openModal('alertSound')}
                  />
                </Tooltip>
              </AlertConfig>

              <Tooltip title="Contacto / Preguntas / Sugerencias">
                <Button
                  type="primary" size="small"
                  icon={<MailOutlined />}
                  onClick={() => contactFormRef.current?.openModal()}
                />
              </Tooltip>

              {/* Divider */}
              <div className="toolbar-divider" />

              <Tooltip title="Server Status">
                <Button
                  type="primary" size="small"
                  icon={<DashboardOutlined />}
                  onClick={() => openModal('status')}
                />
              </Tooltip>

              {/* Admin tools */}
              <Tooltip title="Panel para aprobar accesos">
                <Badge count={requestCount} size="small">
                  <Button
                    type="primary" size="small"
                    icon={<SettingOutlined />}
                    onClick={() => openModal('admin')}
                  />
                </Badge>
              </Tooltip>

              <Tooltip title="Configuración de alertas">
                <Button
                  type="primary" size="small"
                  icon={<AlertOutlined />}
                  onClick={() => openModal('alertConfig')}
                />
              </Tooltip>

              <Tooltip title="Editar lista de enemigos">
                <Button
                  type="primary" size="small"
                  icon={<UnorderedListOutlined />}
                  onClick={() => openModal('classification')}
                />
              </Tooltip>
            </Space>
          </header>

          {/* ── Main tabs ──────────────────────────────────────── */}
          <main className="dashboard-body">
            <Tabs
              defaultActiveKey="Comparativo"
              items={[
                ...GUILDS.map(guild => ({
                  key: guild,
                  label: `${guild} (${onlinePlayers[guild]?.length || 0})`,
                  children: renderGuildTab(guild),
                })),
                {
                  key: 'Comparativo',
                  label: 'Comparativo',
                  children: (
                    <div className="guild-container">
                      {GUILDS.map(guild => (
                        <div key={guild} style={{ flex: 1, minWidth: 0 }}>
                          {renderGuildTab(guild)}
                        </div>
                      ))}
                    </div>
                  ),
                },
              ]}
            />
          </main>
        </div>

        {/* ── Modals (rendered outside layout flow) ──────────── */}
        <CharacterScanModal
          visible={modals.character}
          onClose={() => closeModal('character')}
          initialName={scanCharacterName}
        />
        <AlertasModal
          visible={modals.alertConfig}
          onClose={() => closeModal('alertConfig')}
          fetchAlertConfig={fetchAlertConfig}
        />
        <ClassificacaoModal
          visible={modals.classification}
          onClose={() => closeModal('classification')}
          fetchClassificacoes={fetchClassificacoes}
          semGuildList={semGuildList}
          updateSemGuildList={setSemGuildList}
        />
        <AdminPanelModal
          visible={modals.admin}
          onClose={() => { closeModal('admin'); reloadRequestCount(); }}
        />
        <StatusModal
          visible={modals.status}
          onClose={() => closeModal('status')}
        />
        <ContactForm ref={contactFormRef} />
      </>
    </ConfigProvider>
  );
}

export default Dashboard;